import React from 'react';
import { Users } from 'lucide-react';
import { fmtKwh, fmtNum } from '../../../../src/lib/funnelUtils';
import { COLUNAS_VENDEDOR } from '../funilPorVendedor';
import type { ColunaVendedor, LinhaVendedor } from '../funilPorVendedor';

const TITULOS: Record<ColunaVendedor, string> = {
    prospeccao: 'Prospecção',
    simulacao: 'Simulação',
    termo_enviado: 'Termo Enviado',
    termo_assinado: 'Termo Assinado',
    em_analise: 'Em Validação de Dados',
    com_pendencia: 'Com Pendência',
    termo_aprovado: 'Termo Aprovado',
    termo_reprovado: 'Termo Reprovado',
};

const CORES: Record<ColunaVendedor, string> = {
    prospeccao: '#3b82f6',
    simulacao: '#a855f7',
    termo_enviado: '#eab308',
    termo_assinado: '#14b8a6',
    em_analise: '#0ea5e9',
    com_pendencia: '#f59e0b',
    termo_aprovado: '#22c55e',
    termo_reprovado: '#ef4444',
};

interface Props {
    linhas: LinhaVendedor[];
    total: LinhaVendedor;
    /** Explica a contagem em uso — a mesma frase do Funil Macro. */
    descricao: string;
    /** Abre a lista de negociações daquela célula. */
    onAbrir: (linha: LinhaVendedor, coluna: ColunaVendedor, titulo: string) => void;
}

/**
 * Funil por vendedor — como cada um está no período, etapa a etapa, em número
 * e em kWh. Cada número abre a lista das negociações que ele soma.
 */
const FunilPorVendedorBlock: React.FC<Props> = ({ linhas, total, descricao, onAbrir }) => {
    const celula = (linha: LinhaVendedor, coluna: ColunaVendedor, ehTotal = false) => {
        const c = linha.celulas[coluna];
        const vazia = c.qtd === 0;
        return (
            <td key={coluna} className="py-1.5 px-1 text-right align-top">
                {vazia ? (
                    <span className="inline-block px-2 py-1 text-gray-300 dark:text-gray-600">—</span>
                ) : (
                    <button
                        type="button"
                        onClick={() => onAbrir(linha, coluna, `${TITULOS[coluna]} — ${linha.nome}`)}
                        title={`Ver as ${c.qtd} negociações`}
                        aria-label={`${linha.nome}, ${TITULOS[coluna]}: ${c.qtd} negociações, ${fmtKwh(c.kwh)}. Ver a lista`}
                        className="w-full text-right px-2 py-1 rounded-lg hover:bg-primary/5 dark:hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors"
                    >
                        <span className={`block tabular-nums ${ehTotal ? 'font-extrabold' : 'font-bold'} ${coluna === 'termo_reprovado' ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{fmtNum(c.qtd)}</span>
                        <span className="block text-[11px] font-semibold tabular-nums text-blue-600 dark:text-blue-400 whitespace-nowrap">{fmtKwh(c.kwh)}</span>
                    </button>
                )}
            </td>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Users size={15} className="text-primary" /> Funil por Vendedor
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {descricao} Clique num número para ver as negociações.
            </p>
            {linhas.length === 0 ? (
                <p className="text-xs text-gray-400">Nenhuma negociação no período com os filtros atuais.</p>
            ) : (
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full min-w-[900px] text-xs">
                        <thead>
                            <tr className="text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                <th className="py-2 px-2 text-left font-semibold sticky left-0 bg-white dark:bg-gray-800">Vendedor</th>
                                {COLUNAS_VENDEDOR.map(c => (
                                    <th key={c} className="py-2 px-2 text-right font-semibold whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CORES[c] }}></span>
                                            {TITULOS[c]}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                            {linhas.map(l => (
                                // Fundo opaco no hover: a coluna do nome é fixa (sticky) e
                                // precisa do mesmo fundo da linha para não "furar" na rolagem.
                                <tr key={l.id ?? 'sem'} className="group hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className={`py-2 px-2 font-semibold whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 ${l.id ? 'text-gray-800 dark:text-gray-100' : 'text-amber-600 dark:text-amber-400'}`}>
                                        {l.nome}
                                    </td>
                                    {COLUNAS_VENDEDOR.map(c => celula(l, c))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-200 dark:border-gray-600">
                                <td className="py-2 px-2 font-extrabold text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">Total</td>
                                {COLUNAS_VENDEDOR.map(c => celula(total, c, true))}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FunilPorVendedorBlock;
